import './Infocentrum.less';

import React, { useEffect } from 'react';
import { dispatchSync, useReframe } from 'nike-re-framejs';
import { MATCHTRACKER, VIDEO } from '@/src/player/services/tabConstants';
import { VideoManager } from '@/src/player/views/components/video/VideoManager';
import { RIGHT_COLUMN_TIKET } from '@/src/right-column-ng/components/RightColumnNg';
import {
    getBetradarActiveTab$,
    getBetradarSportCode$,
    getBetradarSportId$,
    getBetradarStreamId$,
    getEventId$,
    getMatch$,
    getTournamentId$,
    getUfcEventId$,
    isBetradarStreamOnTicket$,
    isVideoAvailableForEvent$
} from '@/src/infocentrum/subs';
import BetradarStatisticsTabs, {
    isFullBetradarScoreboard,
    isUFC,
    loadBetradarWidgetsLib,
    MBM
} from 'nike-ui/src/components/match-statistics/utils';
import BetradarMTScoreboard from 'nike-ui/src/components/betradar-widgets/BetradarMTScoreboard';
import clsx from 'clsx';
import RightColumnStreamsTabs from '@/src/right-column-ng/components/RightColumnStreamsTabs';
import BetradarMTAnimation from 'nike-ui/src/components/betradar-widgets/BetradarMTAnimation';
import Tabs, { useTabs } from 'nike-ui/src/components/turboLive/components/Tabs';
import { isLiveEvent$$ } from 'nike-ui/src/components/match-statistics/subs';
import MatchStatisticsContent from 'nike-ui/src/components/match-statistics/views/MatchStatisticsContent';
import MatchStatisticsTabs from 'nike-ui/src/components/match-statistics/views/MatchStatisticsTabs';
import { startVideo } from '@/src/player/player-actions';
import BetradarScoreboardCustomized from 'nike-ui/src/components/betradar-widgets/betradar-scoreboard/BetradarScoreboardCustomized';
import { playerStreamMatchId$ } from '@/src/player/subs';
import { useLoadMatchtrackerTemplates } from 'nike-ui/src/components/match-statistics/hooks/useLoadMatchtrackerTemplates';
import { useIsBetradarWidgetAvailable } from 'nike-ui/src/components/match-statistics/hooks/useIsBetradarWidgetAvailable';
import { hasMbm$, isMbmLoaded$$ } from 'nike-ui/src/components/turboLive/push/detail/subs';

function Infocentrum({ activeTab }: { activeTab? }) {
    useLoadMatchtrackerTemplates();
    const { derefSub, subscribe } = useReframe();
    const betradarStreamId = derefSub(getBetradarStreamId$);
    const sportId = parseInt(derefSub(getBetradarSportId$));
    const isVideoStreamAvailable = derefSub(isVideoAvailableForEvent$);
    const sportCode = derefSub(getBetradarSportCode$);
    const eventId = derefSub(getEventId$);
    const tournamentId = String(derefSub(getTournamentId$));
    const ufcEventId = derefSub(getUfcEventId$);
    const isBetradarTicketTabActive = derefSub(getBetradarActiveTab$) === RIGHT_COLUMN_TIKET;
    const isBetradarStreamOnTicket = derefSub(isBetradarStreamOnTicket$);
    const isLiveEvent = !!derefSub(isLiveEvent$$);
    const isMbmLoaded = subscribe(isMbmLoaded$$);
    const match = derefSub(getMatch$);
    const hasMbm = subscribe(hasMbm$(eventId));
    const autoplayVideoStream = !window.location.pathname.startsWith('/live-stavky');
    const runningMatchId = derefSub(playerStreamMatchId$);
    useEffect(() => {
        loadBetradarWidgetsLib();
    }, []);
    const infocentrumTabs = useIsBetradarWidgetAvailable(match);
    const betradarWidgetAvailable = betradarStreamId && infocentrumTabs?.size;

    useEffect(() => {
        dispatchSync(['player.init']);
        return () => dispatchSync(['player.clean']);
    }, []);
    useEffect(() => {
        if (
            isVideoStreamAvailable &&
            ((autoplayVideoStream && isLiveEvent) ||
                (isLiveEvent && runningMatchId && betradarStreamId !== runningMatchId))
        ) {
            startVideo(eventId, null);
        }
    }, [autoplayVideoStream, runningMatchId, eventId, betradarStreamId, isLiveEvent, isVideoStreamAvailable]);

    const initialActive =
        isMbmLoaded &&
        (hasMbm ? MBM : betradarWidgetAvailable ? BetradarStatisticsTabs[infocentrumTabs?.get(0)] : null);

    const [
        onTabClick,
        setArrowVisible,
        wrapRef,
        scrollElementRef,
        active,
        leftArrow,
        rightArrow,
        triggerRepaint
    ] = useTabs(initialActive);

    //musi to tu byt pre pripad, ked preklikavam medzi podujatiami, a menia sa mi taby, a mal som vybrany tab, ktory teraz nemam dostupny
    useEffect(() => {
        onTabClick(null, initialActive);
    }, [infocentrumTabs, initialActive, onTabClick]);
    const showTabs = !isBetradarTicketTabActive && (hasMbm || betradarWidgetAvailable);

    return (
        <div
            data-participants={match?.participants}
            data-tournament={match?.tournament}
            data-sport={match?.sport}
            data-game-state={isLiveEvent ? 'LIVE' : 'PREMATCH'}
        >
            <div
                className={clsx('infocentre-animation-hidder p-rel', {
                    'infocentre-animation-hidder-visible': isBetradarStreamOnTicket || !isBetradarTicketTabActive,
                    'br-b-default-light': !showTabs
                })}
            >
                {!isBetradarTicketTabActive ? (
                    isFullBetradarScoreboard(sportId) && betradarWidgetAvailable && betradarStreamId ? (
                        <BetradarMTScoreboard matchId={betradarStreamId} sportId={sportId} />
                    ) : (
                        <BetradarScoreboardCustomized
                            sportId={sportId}
                            eventId={eventId}
                            sportCode={sportCode}
                            match={match}
                            streamId={betradarStreamId}
                        />
                    )
                ) : null}
                {(isBetradarTicketTabActive ? isBetradarStreamOnTicket : activeTab === VIDEO) && isLiveEvent && (
                    <VideoWithStats matchId={autoplayVideoStream ? null : eventId} />
                )}
                {!isBetradarTicketTabActive &&
                    activeTab === MATCHTRACKER &&
                    (betradarWidgetAvailable && betradarStreamId && !isUFC(sportId) ? (
                        <BetradarMTAnimation
                            matchId={betradarStreamId}
                            momentumDisabled={isBetradarTicketTabActive}
                            sportId={`${sportId}`}
                        />
                    ) : null)}
            </div>

            {showTabs && (
                <>
                    <div className="flex infocentre-tabs br-t-default-light" data-atid="infocentre-tabs">
                        <RightColumnStreamsTabs />
                        <div className="flex-1 flex flex-col">
                            <Tabs
                                leftArrowVisible={leftArrow}
                                rightArrowVisible={rightArrow}
                                setArrowVisible={setArrowVisible}
                                wrapRef={wrapRef}
                                otherClass="right-column-ng-tabs-shadow"
                                isLiveEvent={isLiveEvent}
                            >
                                <MatchStatisticsTabs
                                    scrollElementRef={scrollElementRef}
                                    active={active}
                                    onTabClick={onTabClick}
                                    sportId={sportId}
                                    infocentrumTabs={betradarWidgetAvailable ? infocentrumTabs : null}
                                    eventId={eventId}
                                    callback={triggerRepaint}
                                />
                            </Tabs>
                        </div>
                    </div>
                    <MatchStatisticsContent
                        active={active}
                        matchId={betradarStreamId}
                        sportId={sportId}
                        eventId={eventId}
                        sportCode={sportCode}
                        activeTab={activeTab}
                        ufcEventId={ufcEventId}
                        tournamentId={tournamentId}
                    />
                </>
            )}
        </div>
    );
}

const VideoWithStats = ({ matchId }) => (
    <div className="p-rel betradar-animation-embeded">
        <VideoManager matchId={matchId} />
    </div>
);

export default Infocentrum;
